import { useCallback, useEffect, useState } from 'react';
import { MasterdataError } from '../masterdata/repository';
import { requestFailure } from './presentation';

/** Retain the current query during refresh; never display a different query's data. */
export function useRemote<T>(
  load: (signal: AbortSignal) => Promise<T>,
  enabled = true,
  clearOnError = false
) {
  const [result, setResult] = useState<{ owner: typeof load; data: T } | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(enabled);
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    setBusy(true);
    setError('');
    load(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setResult({ owner: load, data });
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) {
          setError(requestFailure(cause));
          if (
            clearOnError ||
            (cause instanceof MasterdataError && [401, 403].includes(cause.status))
          )
            setResult(null);
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setBusy(false);
      });
    return () => controller.abort();
  }, [load, enabled, revision, clearOnError]);
  const retry = useCallback(() => setRevision((value) => value + 1), []);
  return { data: result?.owner === load ? result.data : null, error, busy, retry };
}
